import type { EditorContextProps } from '@tunasong/editor'
import { EntityEditor } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Delete, Performance } from '@tunasong/icons'
import type { Note, NoteType } from '@tunasong/models'
import { isNote } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import { isPersisted } from '@tunasong/schemas'
import { ConfirmDialog, Button, ButtonGroup} from '@tunasong/ui-lib'
import { useCallback, useState } from 'react'

export interface NoteEditorProps extends EditorContextProps {
  controls?: boolean
}

export const NoteEditor = (props: NoteEditorProps) => {
  const { element, controls = false, ...restProps } = props
  const [showDelete, setShowDelete] = useState(false)
  const notePlugins = usePlugins('note')
  // Use upsert to create notes when they don't exist
  const updateEntity = graphHooks.useEntityUpdate<Note>({ debounceDelay: 1 * 1000, upsert: true })

  const handleType = useCallback(
    (noteType: NoteType) => () => {
      if (!isPersisted(element)) {
        return
      }
      updateEntity(element.id, { noteType })
    },
    [element, updateEntity]
  )

  const trash = graphHooks.useEntityTrash(element as never)
  const handleDelete = () => {
    if (!(isNote(element) && isPersisted(element))) {
      return
    }
    setShowDelete(false)
    trash()
  }

  const handleShowDelete = () => setShowDelete(true)
  const handleHideDelete = () => setShowDelete(false)

  if (!(isNote(element) && notePlugins)) {
    return null
  }

  const editor = (
    <EntityEditor<Note> {...restProps} element={element} upsert={true} plugins={notePlugins} gutter={false} />
  )

  const { noteType } = element

  return !controls ? (
    editor
  ) : (
    <div style={{ flex: 1, flexDirection: 'column' }}>
      <ButtonGroup variant="text" size="small" aria-label="Note Type">
        <Button title="Performance Note" onClick={handleType('performance')}>
          <Performance
              style={{
                color:
                  noteType === 'performance'
                    ? 'var(--tuna-palette-primary-main)'
                    : 'var(--tuna-palette-action-focus)',
              }}
            />
        </Button>
        <Button title="Delete Note  " onClick={handleShowDelete}>
          <Delete style={{ color: 'var(--tuna-palette-action-focus)' }} />
        </Button>
      </ButtonGroup>
      <div style={{ marginTop: 16 }} />
      {editor}
      <ConfirmDialog
        open={showDelete}
        title="Delete note"
        text={`Really delete ${element.noteType} note?`}
        onCancel={handleHideDelete}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default NoteEditor
